import * as angular from 'angular';
import {
    BalancingAndPortfolioUploadWizardService,
    ShipperAssignmentPreSelection
} from 'app/_services/balancing-and-portfolio-upload-wizard.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { BalancingGroupUploadWizardContext, mgoAngularWizard } from '../custom';
import { NetworkPointResource } from '../_resources/network-point.resource';
import { AuthService } from '../_services/auth.service';

type AcceptDragDropByElemClass = (
    sourceItemHandleScope: any,
    destSortableScope: any,
    elementId: string
) => boolean;

type DragDropListener = {
    itemMoved: () => void;
    accept: AcceptDragDropByElemClass;
};

type SortableScopeClassIdentifier =
    | 'capacity-category-restrictions'
    | 'shipper-assignments';

class BalancingGroupUploadWizardConfigurationComponentCtrl
    implements ng.IController
{
    public wizardCtrl: mgoAngularWizard.Wizard<BalancingGroupUploadWizardContext>;
    public dragDropListenerShipper: DragDropListener;
    public dragDropListenerCapacity: DragDropListener;
    public shippersWithRetiredAssignment: Shipper[];
    public minRuntimeStart: Date;
    private authenticatedUser: LoginResult;
    public autocompleteLoading: boolean = false;

    constructor(
        private $scope: ng.IScope,
        private balancingAndPortfolioUploadWizardService: BalancingAndPortfolioUploadWizardService,
        private COLUMN_FILTER,
        private errorHandler: ErrorHandler,
        private NetworkPoint: NetworkPointResource,
        private authService: AuthService
    ) {
        this.registerDragDropListeners();
        this.minRuntimeStart = new Date();
    }

    $onInit(): void {
        this.authService.userLoginPromise.promise.then(
            (loginResult: LoginResult) => {
                this.authenticatedUser = loginResult;
            }
        );
        this.shippersWithRetiredAssignment = [];

        // race condition! shippersWithAssignmentToTso is not always loaded at this point
        if (this.wizardCtrl.context.shippersWithAssignmentToTso) {
            this.preSelectShipperAssignments();
        } else {
            this.$scope.$watch(
                () => this.wizardCtrl.context.shippersWithAssignmentToTso,
                (newValue, oldValue) => {
                    if (newValue && !oldValue) {
                        this.preSelectShipperAssignments();
                    }
                }
            );
        }

        this.preSelectCapacityCategory();
        this.wizardCtrl.context.currentBalancingGroup.validity =
            this.balancingAndPortfolioUploadWizardService.preSetValidity(
                this.wizardCtrl.context.currentBalancingGroup.validity,
                (this.wizardCtrl.context.currentBalancingGroup as any).$$exists
            );
    }

    private registerDragDropListeners() {
        this.dragDropListenerShipper = {
            itemMoved: () =>
                (this.wizardCtrl.context.currentBalancingGroup.assignedShippers =
                    this.balancingAndPortfolioUploadWizardService.mapSelectedShippersToModel(
                        this.wizardCtrl.context.selectedShippers
                    )),
            accept: (
                sourceItemHandleScope: any,
                destSortableScope: any
            ): boolean =>
                this.isValidDestinationSortableScope(
                    sourceItemHandleScope,
                    destSortableScope,
                    'shipper-assignments'
                )
        };
        this.dragDropListenerCapacity = {
            itemMoved: () =>
                (this.wizardCtrl.context.currentBalancingGroup.capacityCategoryRestrictions =
                    this.wizardCtrl.context.selectedCapacityCategories),
            accept: (
                sourceItemHandleScope: any,
                destSortableScope: any
            ): boolean =>
                this.isValidDestinationSortableScope(
                    sourceItemHandleScope,
                    destSortableScope,
                    'capacity-category-restrictions'
                )
        };
    }

    private isValidDestinationSortableScope(
        sourceItemHandleScope: any,
        destSortableScope: any,
        elementClassIdentifier: SortableScopeClassIdentifier
    ): boolean {
        return (
            angular
                .element(sourceItemHandleScope.sortableScope.element)
                .hasClass(elementClassIdentifier) &&
            angular
                .element(destSortableScope.element)
                .hasClass(elementClassIdentifier)
        );
    }

    private preSelectCapacityCategory() {
        if (
            !this.wizardCtrl.context.currentBalancingGroup
                .capacityCategoryRestrictions
        ) {
            this.wizardCtrl.context.currentBalancingGroup.capacityCategoryRestrictions =
                [];
            return;
        }

        this.wizardCtrl.context.selectedCapacityCategories =
            this.wizardCtrl.context.currentBalancingGroup.capacityCategoryRestrictions;
        this.wizardCtrl.context.unselectedCapacityCategories =
            this.wizardCtrl.context.allCapacityCategories.filter(
                (capacityCategoryFromAll: CapacityCategory) =>
                    !this.wizardCtrl.context.currentBalancingGroup.capacityCategoryRestrictions.find(
                        (capacityCategoryFromCurrent: CapacityCategory) =>
                            capacityCategoryFromCurrent ===
                            capacityCategoryFromAll
                    )
            );
    }

    private preSelectShipperAssignments() {
        if (!this.wizardCtrl.context.currentBalancingGroup.assignedShippers) {
            return;
        }

        const shipperAssignmentPreSelection: ShipperAssignmentPreSelection =
            this.balancingAndPortfolioUploadWizardService.preSelectShipperAssignments(
                this.wizardCtrl.context.currentBalancingGroup.assignedShippers,
                this.wizardCtrl.context.shippersWithAssignmentToTso
            );

        this.shippersWithRetiredAssignment =
            shipperAssignmentPreSelection.shippersWithRetiredAssignment;
        this.wizardCtrl.context.unselectedShippers =
            shipperAssignmentPreSelection.unselectedShippers;
        this.wizardCtrl.context.selectedShippers =
            shipperAssignmentPreSelection.selectedShippers;
        this.wizardCtrl.context.currentBalancingGroup.assignedShippers =
            this.balancingAndPortfolioUploadWizardService.mapSelectedShippersToModel(
                shipperAssignmentPreSelection.selectedShippers
            );
    }

    public loadAutoSuggestNetworkPoints(query) {
        this.autocompleteLoading = true;
        let returnPromise = this.NetworkPoint.query(
            {
                mode: 'filtered',
                infix: query,
                tsoNames: [this.authenticatedUser.tsoName],
                bundleType: 'UNBUNDLED',
                detailLevel: 'MINIMAL'
            },
            (res) => {},
            this.errorHandler
        ).$promise.finally(() => (this.autocompleteLoading = false));
        return returnPromise;
    }

    public submit(isValid): boolean {
        if (!isValid) {
            return false;
        }
        this.wizardCtrl.next();

        return true;
    }
}

angular.module('app').component('pkpBalancingGroupUploadWizardConfiguration', {
    controller: BalancingGroupUploadWizardConfigurationComponentCtrl,
    template:
        require('app/balancing-group-upload-wizard/balancing-group-upload-wizard-configuration.component.html'),
    require: {
        wizardCtrl: '^^wizard'
    },
    bindings: {}
});
